<template>
  <v-form ref="form" class="form">
    <p class="pa-4 ma-0">{{ $t("manage-records.delete-records-about") }}</p>

    <v-divider></v-divider>
    <v-row class="px-0 px-4">
      <v-col cols="6" class="py-0">
        <DateInput
          :date="start_time"
          :label="$t('manage-records.start-date')"
          @update="val => (start_time = val)"
          :rules="[rules.requiredForDate, backEndValidation('start_time')]"
        ></DateInput>
      </v-col>
      <v-col cols="6" class="py-0">
        <DateInput
          :date="end_time"
          :label="$t('manage-records.end-date')"
          @update="val => (end_time = val)"
          :rules="[rules.requiredForDate, backEndValidation('end_time')]"
        ></DateInput>
      </v-col>
      <div class="flex">
        <v-checkbox
          v-model="modified"
          color="primary"
          :label="$t('meter-units.keep-modified')"
          class="checkbox"
        ></v-checkbox>
      </div>
      <v-col cols="12">
        <v-autocomplete
          chips
          multiple
          v-model="realSelectedMeterUnits"
          :items="meterUnits"
          :item-text="fullPath"
          item-value="id"
          :label="$t('meter-units.meter-units')"
          :rules="[rules.required]"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 3">
              <span>{{ item.name }}</span>
            </v-chip>
            <span v-if="index === 3" class="ma-1 grey--text caption"
              >(+{{ realSelectedMeterUnits.length - 3 }}
              {{ $t("others") }})</span
            >
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="6">
        <v-checkbox
          v-model="aligned"
          color="primary"
          :label="$t('meter-units.aligned')"
          class="checkbox"
          :rules="[requiredWithOneSelected]"
        ></v-checkbox
      ></v-col>
      <v-col cols="6">
        <v-checkbox
          v-model="raw"
          color="primary"
          :label="$t('meter-units.raw')"
          class="checkbox"
          :rules="[requiredWithOneSelected]"
        ></v-checkbox
      ></v-col>
      <v-col v-show="!hideSaveButton">
        <v-btn color="primary mr-3" v-on:click="save()" :loading="loading">
          <v-icon left>mdi-trash-can-outline</v-icon>
          {{ $t(`manage-records.delete-records`) }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { rules } from "../../../mixins/rules";
import DateInput from "../../../common/inputs/DateInput";
import moment from "moment";
export default {
  name: "DeleteRecords",
  components: { DateInput },
  mixins: [rules],
  props: {
    meterUnitList: {
      type: Array,
      default: function() {
        return [];
      }
    },
    meterUnits: {
      type: Array
    },
    selectedMeterUnits: {
      type: [Boolean, Array],
      default: function() {
        return false;
      }
    },
    hideSaveButton: {
      type: Boolean,
      default: false
    },
    successCallback: {
      type: Function,
      default: function() {
        return new Function();
      }
    },
    errorCallback: {
      type: Function,
      default: function() {
        return new Function();
      }
    }
  },
  data: () => ({
    loading: false,
    start_time: "",
    end_time: "",
    record_types: ["raw", "aligned"],
    modified: true,
    realSelectedMeterUnits: [],
    raw: true,
    aligned: true
  }),
  computed: {
    project() {
      return this.$store.getters["projectStore/getItem"];
    },
    requiredWithOneSelected() {
      return () => {
        const falseCount = [this.raw, this.aligned].filter(
          value => value === false
        ).length;
        return (
          falseCount < 2 || this.$t("manage-records.at-least-one-checkbox")
        );
      };
    }
  },
  watch: {
    meterUnits: {
      handler: function() {
        this.updateSelected();
      },
      deep: true
    },
    selectedMeterUnits: {
      handler: function() {
        this.updateSelected();
      },
      deep: true
    },
    raw(newValue) {
      if (newValue) {
        if (!this.record_types.includes("raw")) {
          this.record_types.push("raw");
        }
      } else {
        const index = this.record_types.indexOf("raw");
        if (index !== -1) {
          this.record_types.splice(index, 1);
        }
      }
    },
    aligned(newValue) {
      if (newValue) {
        if (!this.record_types.includes("aligned")) {
          this.record_types.push("aligned");
        }
      } else {
        const index = this.record_types.indexOf("aligned");
        if (index !== -1) {
          this.record_types.splice(index, 1);
        }
      }
    }
  },
  methods: {
    save() {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;
        let meter_units = [];
        if (this.meterUnitList.length) {
          meter_units = [...this.meterUnitList.map(i => i.id)];
        } else {
          meter_units = [this.$route.params.id];
        }
        let params = {
          end_time: moment
            .utc(moment.tz(this.end_time, this.project.timezone).endOf("day"))
            .format("YYYY-MM-DDTHH:mm:ss"),
          start_time: moment
            .utc(
              moment.tz(this.start_time, this.project.timezone).startOf("day")
            )
            .format("YYYY-MM-DDTHH:mm:ss"),
          project_id: this.$route.params.project_id,
          meter_units,
          record_types: this.record_types,
          keep_modified: this.modified
        };
        this.$store
          .dispatch(
            "projectMeteringStore/meterUnitRecordStore/massDeleteRecords",
            {
              params,
              errorHandler: this.errorHandler.bind(this, () => {
                this.$refs.form.validate();
              }),
              onSuccess: () => {
                this.successCallback();
              },
              onError: this.errorCallback
            }
          )
          .finally(() => (this.loading = false));
      }
    },
    fullPath(i) {
      return [
        ...i.path.reduce((ac, i) => {
          ac.push(i.name);
          return ac;
        }, []),
        i.name
      ].join(" > ");
    },
    updateSelected() {
      if (this.selectedMeterUnits) {
        this.realSelectedMeterUnits = this.selectedMeterUnits.map(i => i.id);
      } else {
        this.realSelectedMeterUnits = this.meterUnits?.map(i => i.id);
      }
    }
  },
  mounted() {
    this.updateSelected();
    this.$route.params.project_id &&
      this.$store.dispatch("projectStore/get", {
        id: this.$route.params.project_id
      });
  }
};
</script>
